import { IconButton } from '@/shared/ui';
import { XCircleIcon } from 'lucide-react';
import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AttributeResponse } from '@/entities/attributes';
import { NestedItemCard } from '@/features/nested-items';
import {
  AttributeGroupFormValues,
  GroupAttributeValue,
  isSameGroupAttribute,
  toGroupAttributeValue,
} from '../AttributeGroupForm/types';
import { AddAttributeDialog } from './AddAttributeDialog';

const getAttributeName = (attribute: GroupAttributeValue) => attribute.name;
const getAttributeCode = (attribute: GroupAttributeValue) => attribute.code;
const getAttributeDescription = (attribute: GroupAttributeValue) => attribute.description;

type Props = {
  disabled?: boolean;
};

export const AttributesList: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation(['attributeGroups', 'common']);
  const { setValue, getValues, watch } = useFormContext<AttributeGroupFormValues>();
  // Строки состава — из формы: справочник атрибутов ради подписей не запрашивается
  const currentAttributes = watch('attributes');

  // Состав группы — такое же изменение карточки, как правка её поля: без `shouldDirty` кнопка сохранения остаётся неактивной
  const handleSaveDialog = (newAttributes: AttributeResponse[]) => {
    setValue('attributes', [...(getValues('attributes') || []), ...newAttributes.map(toGroupAttributeValue)], {
      shouldDirty: true,
    });
  };

  const handleRemoveAttribute = (attribute: GroupAttributeValue) => {
    setValue('attributes', getValues('attributes')?.filter((attr) => !isSameGroupAttribute(attr, attribute)) || [], {
      shouldDirty: true,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-5 items-center">
        <p className="font-medium">{t('attributes')}</p>

        {!disabled && <AddAttributeDialog existingAttributes={currentAttributes ?? []} onSave={handleSaveDialog} />}
      </div>

      <div className="flex flex-col gap-3">
        {currentAttributes?.map((attribute) => (
          <NestedItemCard
            // Код повторяется у вложенного атрибута и корневого «тёзки»: строку называют код и структура
            key={`${attribute.parentStructCode}.${attribute.code}`}
            item={attribute}
            renderTitle={getAttributeName}
            renderHint={getAttributeCode}
            renderDescription={getAttributeDescription}
            action={
              !disabled && (
                <IconButton
                  icon={<XCircleIcon />}
                  color="destructive"
                  label={t('attributeGroups:removeAttributeFromGroup')}
                  onClick={() => handleRemoveAttribute(attribute)}
                />
              )
            }
          />
        ))}
        {currentAttributes?.length === 0 && <p className="text-sm text-muted-foreground">{t('noAttributesInGroup')}</p>}
      </div>
    </div>
  );
};
